import { formatDate } from "@/lib/utils";
import {
  Tenant,
  User,
  Host,
  Task,
  NonEmptyArray,
  App,
  Require,
  ScheduledBackupPeriodType
} from "@/types";
import { z } from "zod";

export type TransportType = "slack" | "telegram" | "discord" | "mail" | "webhook";

export const TransportTypes: NonEmptyArray<TransportType> = [
  "slack",
  "telegram",
  "discord",
  "mail",
  "webhook"
];

export const TransportTypeCheck = z.enum(TransportTypes);

export interface MessageMetadata {
  id: string;
  tags?: string[];
  category?: string;
  type: SystemMessageType;
  tenantId: string;
  objectId?: string;
  objectType?: "app" | "host" | "user" | "tenant" | "task" | "test" | "unknown";
  /** Project ID of the app */
  projectId?: string;
  /** App ID of the app */
  appId?: string;
  transport?: TransportType;
  threadId?: string;
  replyToId?: string;
  sentAt?: Date;
  expiresAt?: Date;
  retryCount?: number;
  maxRetries?: number;
  /** Whether to show link previews in the message, if supported by the transport. */
  previews?: boolean;
}

export interface Message {
  metadata: MessageMetadata;
  title?: string;
  body: string;
  object?: { id: string };
  /** Additional properties added as a list to the message body */
  [key: string]: unknown;
}

export const MessageSchema = z
  .object({
    metadata: z.object({
      id: z.string(),
      tags: z.array(z.string()).optional(),
      category: z.string().optional(),
      type: z.string(),
      tenantId: z.string(),
      objectId: z.string().optional(),
      objectType: z
        .enum(["app", "host", "user", "tenant", "task", "unknown", "test"])
        .optional(),
      projectId: z.string().optional(),
      appId: z.string().optional(),
      transport: TransportTypeCheck.optional(),
      threadId: z.string().optional(),
      replyToId: z.string().optional(),
      sentAt: z.date().optional(),
      expiresAt: z.date().optional(),
      retryCount: z.number().optional(),
      maxRetries: z.number().optional(),
      previews: z.boolean().optional()
    }),
    title: z.string().optional(),
    body: z.string(),
    object: z.object({ id: z.string() }).optional()
  })
  .optional();

export interface MessageResult {
  success: boolean;
  /** Tenant ID of the sender, if known */
  tenantId?: string;
  /** Metadata from the Message */
  metadata?: MessageMetadata;
  /** Which transport was actually used */
  transport: TransportType;
  /** Time taken to deliver the message */
  elapsed: number; // in milliseconds
  /** Transport specific message ID from the response */
  messageId?: string;
  /** Timestamp of the delivery or failure */
  timestamp: Date;
  /** Error that occurred during delivery */
  error?: Error;
  rateLimited?: boolean;
  retryAfter?: number; // in milliseconds
}

/** Format additional properties as Markdown list */
export const formatMessageProperties = (message: Message) => {
  // Format the content
  let list = "";
  const keys = Object.keys(message).filter(
    key => key !== "metadata" && key !== "body" && key !== "title" && key !== "object"
  );

  for (const key of keys) {
    let value = message[key];
    if (typeof value === "object" && value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value = value.join(", ");
      } else if (value instanceof Date) {
        value = formatDate(value, "yyyy-MM-dd HH:mm:ss");
      } else {
        value = Object.entries(value)
          .filter(([_, value]) => value !== null && value !== undefined)
          .map(([key, value]) => `${key}=${value}`)
          .join(", ");
      }
    }

    if (value !== undefined && value !== null && value !== "") {
      list += `* **${key}**: ${value}\n`;
    }
  }

  return list;
};

export interface Transport {
  type: TransportType;
  send(message: Message): Promise<MessageResult>;
}

/** Transport formatter which gets Markdown and outputs a variant of markdown, or markup which the transport supports. */
export interface TransportFormatter {
  format(markdown: string): {
    result: string;
    type: "markdown" | "html";
  };
}

export class MessageError extends Error {
  public readonly transport?: TransportType;
  public readonly statusCode?: number;
  public readonly rateLimited?: boolean;
  public readonly retryAfter?: number;

  constructor(
    message: string,
    options?: {
      transport?: TransportType;
      statusCode?: number;
      rateLimited?: boolean;
      retryAfter?: number;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = "MessageError";
    this.transport = options?.transport;
    this.statusCode = options?.statusCode;
    this.rateLimited = options?.rateLimited;
    this.retryAfter = options?.retryAfter;
    this.cause = options?.cause;
  }
}

type MessageArg1<T extends string, I extends string, P> =
  [T, I, P, MessageMetadata] | [T, I, P];

/** Strongly typed types of messages we send with payloads */
export type SystemMessageHandler = <I extends string>(
  tenantId: string,
  ...args:
    | MessageArg1<SystemMessageType.TenantCreated, I, Require<Partial<Tenant>, "id">>
    | MessageArg1<SystemMessageType.UserCreated, I, Require<Partial<User>, "id">>
    | MessageArg1<SystemMessageType.UserOnboarded, I, Require<Partial<User>, "id">>
    | MessageArg1<SystemMessageType.UserTestNotify, I, Require<Partial<User>, "id">>
    | MessageArg1<SystemMessageType.UserStripeCustomerCreated, I, Require<Partial<User>, "id">>
    | MessageArg1<SystemMessageType.HostCreated, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostConnected, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostDeleted, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostProvisioning, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostProvisioned, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostDeprovisioned, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.HostPrepared, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.AppBuilding, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.AppBuilt, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.AppDeploying, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.AppDeployed, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.TaskFailed, I, Require<Partial<Task>, "id">>
    | MessageArg1<SystemMessageType.UserReferralAccepted, I, Require<Partial<User>, "id">>
    | MessageArg1<SystemMessageType.HostBackupComplete, I, Require<Partial<Host>, "id">>
    | MessageArg1<SystemMessageType.AppBackupComplete, I, Require<Partial<App>, "id">>
    | MessageArg1<
        SystemMessageType.ScheduledHostBackupComplete,
        I,
        Require<Partial<Host>, "id">
      >
    | MessageArg1<SystemMessageType.ScheduledAppBackupComplete, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.AppRemoved, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.AppDeleted, I, Require<Partial<App>, "id">>
    | MessageArg1<SystemMessageType.TenantLog, I, Require<Partial<Tenant>, "id">>
) => Promise<void>;

/** Types of messages we send */
export enum SystemMessageType {
  TenantCreated = "tenant-created",
  TenantLog = "tenant-log",
  UserCreated = "user-created",
  UserOnboarded = "user-onboarded",
  UserTestNotify = "user-test-notify",
  UserStripeCustomerCreated = "user-stripe-customer-created",
  UserReferralAccepted = "user-referral-accepted",
  HostCreated = "host-created",
  HostConnected = "host-connected",
  HostDeleted = "host-deleted",
  HostProvisioning = "host-provisioning",
  HostProvisioned = "host-provisioned",
  HostDeprovisioned = "host-deprovisioned",
  HostPrepared = "host-prepared",
  HostBackupComplete = "host-backup-complete",
  ScheduledHostBackupComplete = "scheduled-host-backup-complete",
  AppBuilding = "app-building",
  AppBuilt = "app-built",
  AppDeploying = "app-deploying",
  AppDeployed = "app-deployed",
  AppBackupComplete = "app-backup-complete",
  ScheduledAppBackupComplete = "scheduled-app-backup-complete",
  AppRemoved = "app-removed",
  AppDeleted = "app-deleted",
  TaskFailed = "task-failed",
  TaskDailyReport = "task-daily-report",
  TaskWeeklyReport = "task-weekly-report",
  TaskMonthlyReport = "task-monthly-report"
}

export const SystemMessageTypes: NonEmptyArray<SystemMessageType> = Object.values(
  SystemMessageType
) as NonEmptyArray<SystemMessageType>;

/** Narrow message type a backup-complete notification can take, per target. */
type BackupCompleteMessageType<T extends "host" | "app"> = T extends "host"
  ? SystemMessageType.HostBackupComplete | SystemMessageType.ScheduledHostBackupComplete
  : SystemMessageType.AppBackupComplete | SystemMessageType.ScheduledAppBackupComplete;

/**
 * Picks the backup-complete message type for a target. Scheduled backups (those
 * fulfilling one or more periods) use the dedicated scheduled message type so
 * users can tell automated backups apart from manual ones. The return type stays
 * narrow per target so it slots into the discriminated systemMessage signature.
 */
export const backupCompleteMessageType = <T extends "host" | "app">(
  target: T,
  scheduled?: ScheduledBackupPeriodType[] | null
): BackupCompleteMessageType<T> => {
  const isScheduled = !!scheduled && scheduled.length > 0;
  if (target === "host") {
    return (
      isScheduled
        ? SystemMessageType.ScheduledHostBackupComplete
        : SystemMessageType.HostBackupComplete
    ) as BackupCompleteMessageType<T>;
  }

  return (
    isScheduled
      ? SystemMessageType.ScheduledAppBackupComplete
      : SystemMessageType.AppBackupComplete
  ) as BackupCompleteMessageType<T>;
};

/** These are never sent to the user, they are only sent via system transport */
export const SystemMessageInternal: NonEmptyArray<SystemMessageType> = [
  SystemMessageType.UserCreated,
  SystemMessageType.UserOnboarded,
  SystemMessageType.TenantCreated,
  SystemMessageType.UserTestNotify,
  SystemMessageType.UserStripeCustomerCreated,
  SystemMessageType.TenantLog
];

export const SystemMessageTypeTitles: Record<SystemMessageType, string> = {
  [SystemMessageType.TenantCreated]: "🏢 Tenant created",
  [SystemMessageType.UserCreated]: "👤 User created",
  [SystemMessageType.UserOnboarded]: "👋 User onboarded",
  [SystemMessageType.UserTestNotify]: "🔔 Test notification",
  [SystemMessageType.UserStripeCustomerCreated]: "👤 Stripe customer created",
  [SystemMessageType.HostCreated]: "🆕 Host created",
  [SystemMessageType.HostConnected]: "✅ Host connected",
  [SystemMessageType.HostDeleted]: "🚮 Host deleted",
  [SystemMessageType.HostProvisioning]: "🛠️ Host provisioning started",
  [SystemMessageType.HostProvisioned]: "🛠️ Host provisioned",
  [SystemMessageType.HostDeprovisioned]: "🚮 Host deprovisioned",
  [SystemMessageType.HostPrepared]: "💫 Host prepared",
  [SystemMessageType.AppBuilding]: "📦 App building",
  [SystemMessageType.AppBuilt]: "📦 App built",
  [SystemMessageType.AppDeploying]: "🚀 App deploying",
  [SystemMessageType.AppDeployed]: "🚀 App deployed",
  [SystemMessageType.TaskFailed]: "❌ Task failed",
  [SystemMessageType.UserReferralAccepted]: "👥 Referral accepted",
  [SystemMessageType.HostBackupComplete]: "💾 Host backup complete",
  [SystemMessageType.AppBackupComplete]: "💾 App backup complete",
  [SystemMessageType.ScheduledHostBackupComplete]: "🗓️ Scheduled host backup complete",
  [SystemMessageType.ScheduledAppBackupComplete]: "🗓️ Scheduled app backup complete",
  [SystemMessageType.TaskDailyReport]: "🔔 Daily report",
  [SystemMessageType.TaskWeeklyReport]: "🔔 Weekly report",
  [SystemMessageType.TaskMonthlyReport]: "🔔 Monthly report",
  [SystemMessageType.AppRemoved]: "📦 App removed",
  [SystemMessageType.AppDeleted]: "📦 App deleted",
  [SystemMessageType.TenantLog]: "📝 Tenant log"
};

export const SystemMessageTypeCheck = z.enum(SystemMessageTypes);

export interface TransportDiscordConfig {
  webhookUrl: string;
  timeout?: number;
  username?: string;
  avatarUrl?: string;
}

export interface TransportTelegramConfig {
  botToken: string;
  chatId: string;
  apiBaseUrl?: string;
  timeout?: number;
}

export interface TransportSlackConfig {
  webhookUrl: string;
  timeout?: number;
}

export interface TransportMailConfig {
  to: string[];
}

export interface TransportWebhookConfig {
  webhookUrl: string;
  timeout?: number;
}
